"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { buttonPrimary, card, colors, heroBackground, input, radius, shadow } from "@/styles/theme";
import TurnstileWidget, { type TurnstileWidgetHandle } from "@/components/TurnstileWidget";
import { Icon } from "@/components/ui/Icon";
import { PrinciplesShowcase } from "@/components/PrinciplesShowcase";

// Smaller than the shared `input` style, with room on the left for the
// field icon - scoped to this login form rather than changing `input`
// globally for every other form in the app.
const loginInput = { ...input, padding: "8px 12px 8px 38px", fontSize: "15px" };
const loginInputIconWrap = {
  position: "relative" as const,
  marginTop: "6px",
};
const loginInputIcon = {
  position: "absolute" as const,
  left: "10px",
  top: "50%",
  transform: "translateY(-50%)",
};

export default function Home() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [checkingAuth, setCheckingAuth] = useState(true);
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);

  const router = useRouter();

  // Landing on "/" (or a "Back to TravelStory" link) while already logged
  // in used to dump you back on the login form - Paul: go straight to the
  // dashboard instead. Same shallow "does a token exist" check AuthGuard
  // uses elsewhere; an invalid/expired token still gets caught by the
  // first API call on the dashboard.
  useEffect(() => {
    if (localStorage.getItem("token")) {
      router.replace("/dashboard/journeys");
    } else {
      setCheckingAuth(false);
    }
  }, [router]);

  if (checkingAuth) {
    return <div style={heroBackground} />;
  }

  const handleLogin = async (
    e: React.FormEvent
  ) => {
    e.preventDefault();

    try {
      const response = await fetch(
        "/api/auth/login",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email,
            password,
            turnstileToken: turnstileRef.current?.getToken(),
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();

        localStorage.setItem(
          "token",
          data.token
        );

        localStorage.setItem(
          "displayName",
          data.displayName
        );

        localStorage.setItem(
          "isAdmin",
          data.isAdmin ? "true" : "false"
        );

        localStorage.setItem(
          "isSuperAdmin",
          data.isSuperAdmin ? "true" : "false"
        );

        localStorage.setItem(
          "moderatorRole",
          String(data.moderatorRole ?? 0)
        );

        localStorage.setItem(
          "hasAiAccess",
          data.hasAiAccess ? "true" : "false"
        );

        router.push("/dashboard/journeys");
      } else {
        const data = await response.json().catch(() => null);
        setMessage(data?.message ?? "Invalid email or password");
        // Turnstile tokens are single-use - reset so a retry gets a fresh one.
        turnstileRef.current?.reset();
      }
    } catch {
      setMessage(
        "Unable to contact API"
      );
    }
  };

  return (
    <div style={heroBackground}>
    <main
      style={{
        maxWidth: "400px",
        margin: "0 auto",
        padding: "100px 20px 40px",
      }}
    >
      <h1
        style={{
          textAlign: "center",
          marginBottom: "28px",
          fontSize: "28px",
          fontWeight: 800,
          backgroundImage: colors.gradient,
          backgroundClip: "text",
          WebkitBackgroundClip: "text",
          color: "transparent",
        }}
      >
        TravelStory
      </h1>

      <form
        onSubmit={handleLogin}
        style={{ ...card, display: "flex", flexDirection: "column", gap: "14px" }}
      >
        <div>
          <label
            style={{
              fontSize: "13px",
              fontWeight: 600,
              color: colors.textSecondary,
            }}
          >
            Email or username
          </label>
          <div style={loginInputIconWrap}>
            <span style={loginInputIcon}>
              <Icon name="user" size={16} />
            </span>
            <input
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={loginInput}
            />
          </div>
        </div>

        <div>
          <label
            style={{
              fontSize: "13px",
              fontWeight: 600,
              color: colors.textSecondary,
            }}
          >
            Password
          </label>
          <div style={loginInputIconWrap}>
            <span style={loginInputIcon}>
              <Icon name="password" size={16} />
            </span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={loginInput}
            />
          </div>

          <div style={{ textAlign: "right", marginTop: "6px" }}>
            <Link
              href="/forgot-password"
              style={{ fontSize: "13px", color: colors.accentStart }}
            >
              Forgot password?
            </Link>
          </div>
        </div>

        <TurnstileWidget ref={turnstileRef} />

        <button
          type="submit"
          style={{ ...buttonPrimary, width: "100%", marginTop: "6px" }}
        >
          Log in
        </button>

        {message && (
          <p style={{ color: colors.error, fontSize: "14px", margin: 0 }}>
            {message}
          </p>
        )}
      </form>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: "10px",
          marginTop: "20px",
          fontSize: "14px",
        }}
      >
        <Link
          href="/signup"
          style={{
            color: colors.accentStart,
            background: colors.cardBg,
            padding: "8px 16px",
            borderRadius: radius.pill,
            boxShadow: shadow.card,
            textAlign: "center",
            whiteSpace: "nowrap",
          }}
        >
          Create an account
        </Link>

        <Link
          href="/public"
          style={{
            color: colors.accentStart,
            background: colors.cardBg,
            padding: "8px 16px",
            borderRadius: radius.pill,
            boxShadow: shadow.card,
            textAlign: "center",
            whiteSpace: "nowrap",
          }}
        >
          Browse public journeys
        </Link>

        <Link
          href="/faq"
          style={{
            color: colors.accentStart,
            background: colors.cardBg,
            padding: "8px 16px",
            borderRadius: radius.pill,
            boxShadow: shadow.card,
            textAlign: "center",
            whiteSpace: "nowrap",
          }}
        >
          FAQ
        </Link>
      </div>

      <PrinciplesShowcase />
    </main>
    </div>
  );
}